import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Box,
  Text,
  Spinner,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  SimpleGrid,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import TeamLogo from './TeamLogo';
import { getTeamInfoById } from '../utils/getTeamInfo';
import dayjs from 'dayjs';
import { useBoxScore, type TabularSection } from '../hooks/useGameScores';
import type { BroadcastGameRow } from './BroadcastGameModal';

interface BoxScoreModalProps {
  isOpen: boolean;
  onClose: () => void;
  gameId: number | null;
  gameRow: BroadcastGameRow | null;
  league: { conferences?: Array<{ divisions?: Array<{ teams?: Array<{ id: number; name?: string; logo?: string; color_primary?: string; color_secondary?: string; color_text?: string }> }> }> } | null;
}

function scoreBoardLineScoreCellProps(isScoreBoard: boolean, col: string) {
  const isRunsCol = isScoreBoard && String(col).toLowerCase() === 'r';
  const border = isRunsCol
    ? {
        borderLeftWidth: '1px',
        borderLeftStyle: 'solid' as const,
        borderLeftColor: 'gray.500',
      }
    : {};
  if (!isScoreBoard || col === 'team') return border;
  return {
    textAlign: 'center' as const,
    ...border,
  };
}

const numericColumns = new Set([
  'AB',
  'R',
  'H',
  'RBI',
  'BB',
  'SO',
  'E',
  'BF',
  'IP',
  'ER',
  'HR',
  '1b',
  '2b',
  '3b',
  'k',
  'bb',
  'hbp',
  'ibb',
  'WP',
  'PKO',
  'CS',
  'SB',
  'PB',
  'LW',
  'LWF',
]);

const renderSectionTable = (section: TabularSection | undefined, size: 'sm' | 'xs' = 'sm') => {
  if (!section || !section.rows.length) {
    return (
      <Text fontSize="sm" color="gray.500">
        No data.
      </Text>
    );
  }

  const isScoreBoard = section.title === 'ScoreBoard';
  const isBattersSection = section.title.endsWith('Batters');
  const isPitchersSection = section.title.endsWith('Pitchers');

  return (
    <Table size={size} variant="simple">
      <Thead>
        <Tr>
          {section.columns.map((col) => (
            <Th
              key={col}
              isNumeric={numericColumns.has(col) && !isScoreBoard}
              {...scoreBoardLineScoreCellProps(isScoreBoard, col)}
            >
              {isScoreBoard && col === 'team'
                ? ''
                : isBattersSection && col === 'LINEUP'
                ? 'PLAYER'
                : isPitchersSection && col === 'PLAYER'
                ? 'PLAYER'
                : col}
            </Th>
          ))}
        </Tr>
      </Thead>
      <Tbody>
        {section.rows.map((row, idx) => (
          <Tr key={idx}>
            {section.columns.map((col) => {
              const raw = row[col];
              const value = raw == null ? '' : String(raw);
              const isNumeric = numericColumns.has(col);
              const isLineup = col === 'LINEUP';
              const isPitcherName = isPitchersSection && col === 'PLAYER';

              // Indent non-starters in LINEUP column when is_starter is false
              if (isLineup && row.is_starter === false) {
                // Derive full vs short display from the value (e.g. "First Last (POS)")
                const text = value;
                const [namePartRaw, restRaw] = text.split('(');
                const namePart = (namePartRaw || '').trim();
                const restPart = restRaw ? `(${restRaw}` : '';
                const nameTokens = namePart.split(/\s+/).filter(Boolean);
                const lastName = nameTokens.length ? nameTokens[nameTokens.length - 1] : namePart;
                const fullDisplay = text;
                const shortDisplay = lastName ? `${lastName}${restPart ? ' ' + restPart : ''}` : text;
                return (
                  <Td key={col} {...scoreBoardLineScoreCellProps(isScoreBoard, col)}>
                    <Text as="span" pl={4}>
                      <span className="boxscore-player-name-full">* {fullDisplay}</span>
                      <span className="boxscore-player-name-short">* {shortDisplay}</span>
                    </Text>
                  </Td>
                );
              }

              // For LINEUP column (starters + subs), render full and short variants,
              // letting CSS decide which to show based on width.
              if (isLineup) {
                const text = value;
                const [namePartRaw, restRaw] = text.split('(');
                const namePart = (namePartRaw || '').trim();
                const restPart = restRaw ? `(${restRaw}` : '';
                const nameTokens = namePart.split(/\s+/).filter(Boolean);
                const lastName = nameTokens.length ? nameTokens[nameTokens.length - 1] : namePart;
                const fullDisplay = text;
                const shortDisplay = lastName ? `${lastName}${restPart ? ' ' + restPart : ''}` : text;
                return (
                  <Td key={col} {...scoreBoardLineScoreCellProps(isScoreBoard, col)}>
                    <span className="boxscore-player-name-full">{fullDisplay}</span>
                    <span className="boxscore-player-name-short">{shortDisplay}</span>
                  </Td>
                );
              }

              // For pitcher PLAYER column, render full and short variants:
              // full = original string, short = last name only (+ decision suffix when present).
              if (isPitcherName) {
                const text = value;
                const namePart = text.trim();
                const decisionMatch = namePart.match(/\s+\((W|L|S)\)$/i);
                const decisionSuffix = decisionMatch ? decisionMatch[0] : '';
                const baseName = decisionMatch ? namePart.slice(0, -decisionSuffix.length).trim() : namePart;
                const nameTokens = baseName.split(/\s+/).filter(Boolean);
                const lastName = nameTokens.length ? nameTokens[nameTokens.length - 1] : baseName;
                const fullDisplay = text;
                const shortDisplay = `${lastName || baseName}${decisionSuffix}`.trim() || text;
                return (
                  <Td key={col} {...scoreBoardLineScoreCellProps(isScoreBoard, col)}>
                    <span className="boxscore-player-name-full">{fullDisplay}</span>
                    <span className="boxscore-player-name-short">{shortDisplay}</span>
                  </Td>
                );
              }

              return (
                <Td key={col} isNumeric={isNumeric && !isScoreBoard} {...scoreBoardLineScoreCellProps(isScoreBoard, col)}>
                  {value}
                </Td>
              );
            })}
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
};

const BoxScoreModal: React.FC<BoxScoreModalProps> = ({
  isOpen,
  onClose,
  gameId,
  gameRow,
  league,
}) => {
  const bgColor = useColorModeValue('gray.900', 'gray.900');

  const { data, isLoading } = useBoxScore(gameId ?? undefined, isOpen);
  const sections = data?.sections ?? [];
  const scoreBoard = sections.find((s) => s.title === 'ScoreBoard') ?? sections[0];
  const pitchingDecisions = sections.find((s) => s.title === 'Pitching Decisions');
  const awayBatters = sections.find((s) => s.title === 'Away Batters');
  const homeBatters = sections.find((s) => s.title === 'Home Batters');
  const awayPitchers = sections.find((s) => s.title === 'Away Pitchers');
  const homePitchers = sections.find((s) => s.title === 'Home Pitchers');
  const awayBattersExtra = sections.find((s) => s.title === 'Away Batters Extra');
  const homeBattersExtra = sections.find((s) => s.title === 'Home Batters Extra');
  const awayPitchersExtra = sections.find((s) => s.title === 'Away Pitchers Extra');
  const homePitchersExtra = sections.find((s) => s.title === 'Home Pitchers Extra');

  const pitchingDecisionsLine = pitchingDecisions?.rows?.length
    ? pitchingDecisions.rows
        .map((row) => `${String(row.Stat ?? '')}: ${String(row.Value ?? '')}`)
        .join(' • ')
    : '';

  if (!gameRow || !league) {
    return null;
  }

  const awayInfo = getTeamInfoById(gameRow.away_team_id, league as Parameters<typeof getTeamInfoById>[1]);
  const homeInfo = getTeamInfoById(gameRow.home_team_id, league as Parameters<typeof getTeamInfoById>[1]);
  const awayName = awayInfo?.name ?? `Team ${gameRow.away_team_id}`;
  const homeName = homeInfo?.name ?? `Team ${gameRow.home_team_id}`;

  const dateLabel = gameRow.scheduled_date
    ? dayjs(gameRow.scheduled_date).format('MMM D, YYYY')
    : '';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full" isCentered={false}>
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent
        maxW="calc(100vw - 64px)"
        mt="32px"
        maxH="calc(100vh - 160px)"
        bg={bgColor}
        borderRadius="md"
      >
        <ModalCloseButton />
        <ModalBody p={4} display="flex" flexDir="column" minH={0}>
          {/* Title */}
          <Box mb={4} textAlign="center">
            <Text fontSize="md" color="gray.300" mb={1}>
              BOX SCORE:
            </Text>
            <Text fontSize="lg" fontWeight="bold" color="white">
              {awayName} at {homeName}
              {dateLabel ? ` — ${dateLabel}` : ''}
            </Text>
          </Box>

          {/* Content */}
          <Box flex={1} minH={0} overflowY="auto">
            {isLoading ? (
              <Flex justify="center" align="center" h="100%" minH="160px">
                <Spinner size="lg" />
              </Flex>
            ) : (
              <>
                {/* Line score / scoreboard */}
                <Box mb={6} px={4}>
                  {renderSectionTable(scoreBoard)}
                  {pitchingDecisionsLine && (
                    <Box mt={2} textAlign="left" pl={4}>
                      <Text fontSize="sm" color="gray.400" textAlign="left">
                        {pitchingDecisionsLine}
                      </Text>
                    </Box>
                  )}
                </Box>

                {/* Two-column layout for batters and pitchers */}
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} px={4}>
                  <VStack align="stretch" spacing={4}>
                    <Box>
                      <Text fontWeight="bold" color="gray.200" mb={2}>
                        {awayName} Batters
                      </Text>
                      {renderSectionTable(awayBatters, 'xs')}
                      {awayBattersExtra && awayBattersExtra.rows.length > 0 && (
                        <Box mt={2}>
                          {awayBattersExtra.rows.map((row, idx) => (
                            <Text key={idx} fontSize="sm" color="gray.400">
                              {String(row.Stat)}: {String(row.Value)}
                            </Text>
                          ))}
                        </Box>
                      )}
                    </Box>
                    <Box>
                      <Text fontWeight="bold" color="gray.200" mb={2}>
                        {awayName} Pitchers
                      </Text>
                      {renderSectionTable(awayPitchers, 'xs')}
                      {awayPitchersExtra && awayPitchersExtra.rows.length > 0 && (
                        <Box mt={2}>
                          {awayPitchersExtra.rows.map((row, idx) => (
                            <Text key={idx} fontSize="sm" color="gray.400">
                              {String(row.Stat)}: {String(row.Value)}
                            </Text>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </VStack>
                  <VStack align="stretch" spacing={4}>
                    <Box>
                      <Text fontWeight="bold" color="gray.200" mb={2}>
                        {homeName} Batters
                      </Text>
                      {renderSectionTable(homeBatters, 'xs')}
                      {homeBattersExtra && homeBattersExtra.rows.length > 0 && (
                        <Box mt={2}>
                          {homeBattersExtra.rows.map((row, idx) => (
                            <Text key={idx} fontSize="sm" color="gray.400">
                              {String(row.Stat)}: {String(row.Value)}
                            </Text>
                          ))}
                        </Box>
                      )}
                    </Box>
                    <Box>
                      <Text fontWeight="bold" color="gray.200" mb={2}>
                        {homeName} Pitchers
                      </Text>
                      {renderSectionTable(homePitchers, 'xs')}
                      {homePitchersExtra && homePitchersExtra.rows.length > 0 && (
                        <Box mt={2}>
                          {homePitchersExtra.rows.map((row, idx) => (
                            <Text key={idx} fontSize="sm" color="gray.400">
                              {String(row.Stat)}: {String(row.Value)}
                            </Text>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </VStack>
                </SimpleGrid>

                {gameId != null && (
                  <Text mt={4} px={4} fontSize="sm" color="gray.400" textAlign="left">
                    Game ID: {gameId}
                  </Text>
                )}
              </>
            )}
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default BoxScoreModal;
